import React, { useState, ReactNode, useContext } from "react";
import "./Sidebar.scss";
import { Icon, ThemedIcon } from "@me8eon/icons";
import { EnvironmentConfig } from "@me8eon/config";
import { AiAssistContext } from "me-at-eon/src/components/ai-assist-refactored/AiAssist.context";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@me8eon/translation";
import { FocusTrap } from "@me8eon/focus_trap";
import { useUserData } from "@me8eon/react_authentication";
import { useWindowProperties } from "me-at-eon/src/context/WindowContext";
import { useAsyncSessionAndMessages, useSessionAndMessageOps } from "@me8eon/sessions_messages";
import { isDataAs, useQuestionSuggestions } from "@me8eon/async";
import { useFeatureFlag } from "@me8eon/featureflags";

interface SidebarProps {
    sidebar: ReactNode;
    children: ReactNode;
}

interface SidebarBtnProps {
    isExpanded: boolean;
    label: string;
    onClick?: () => void;
    noCursor?: boolean;
    icon: string;
    tooltip: string;
    deleteIcon?: boolean;
    tourId?: string;
}

const SidebarBtn = ({ isExpanded, label, onClick, icon, tooltip, noCursor, deleteIcon, tourId }: SidebarBtnProps) => {
    const translate = useTranslation();
    let showDeleteIcon = false;

    const [state] = useAsyncSessionAndMessages();
    if (isDataAs(state)) {
        showDeleteIcon = state.data.parent.sessions.length > 2;
    }
    const { deleteChild } = useSessionAndMessageOps();

    function deleteAllSessions() {
        if (!isDataAs(state)) return;
        const ids = state?.data?.parent.sessions.map(session => session.id) ?? [];
        deleteChild(...ids)
    }

    return (
        <button className={`sidebar-btn ${noCursor ? "sidebar-btn--no-cursor" : ""}`} onClick={onClick} title={tooltip} id={tourId}>
            {icon === "newchat" ? <ThemedIcon name={icon} /> : <Icon name={icon} />}
            {isExpanded && (
                <div className="label-with-icon">
                    <span className={`sidebar-btn-label ${noCursor ? "sidebar-label--no-cursor" : ""}`}>{label}</span>
                    {showDeleteIcon && deleteIcon && <span className="sidebar-btn-delete-icon" title={translate("chatBot.sessions.clearAll")} onClick={deleteAllSessions}><Icon name="trash_bin_small" style={{ width: "16px", height: "16px" }} /></span>}
                </div>
            )}
        </button>
    )
}

export const Sidebar = ({ sidebar, children }: SidebarProps) => {
    const { screenWidth } = useWindowProperties();
    const isMobile = screenWidth < 768;

    const [open, setOpen] = useState(!isMobile);
    const t = useTranslation();
    const navigate = useNavigate();
    const { handleResetChat, setCurrentAgent } = useContext(AiAssistContext);
    const isAdmin = localStorage.getItem("admin") === "true";
    const [, setQs] = useQuestionSuggestions();
    const userData = useUserData();
    const loggedIn = userData.loggedIn;

    if (!loggedIn) {
        return children;
    }

    const handleClick = () => {
        window.location.href = `mailto:${EnvironmentConfig.meAtEonContactEmail}`;
    };

    const closeForMobile = () => {
        isMobile && setOpen(false);
    }

    const navigationClicked = (link: string) => {
        handleResetChat();
        closeForMobile();
        setCurrentAgent("");
        setQs([]);
        navigate(link);
    }

    return (
        <div className="sidebar-layout">
            <FocusTrap>
                <aside
                    className={`sidebar ${open ? "open" : ""}`}
                >
                    <div className="sidebar-header">
                        <SidebarBtn
                            isExpanded={open}
                            label=""
                            onClick={() => setOpen(!open)}
                            icon={`${open ? "collapse" : "expand_sidebar"}`}
                            tooltip={`${open ? t("chatBot.close") : t("chatBot.open")}`}
                        />
                    </div>
                    <div className="sidebar-body">
                        <div id="tileTour5">
                            <SidebarBtn
                                isExpanded={open}
                                label={"Dashboard"}
                                icon="home"
                                tooltip={"Dashboard"}
                                onClick={() => navigationClicked("/tile")}
                                tourId="tileTour9"
                            />
                            {isAdmin && (<SidebarBtn
                                isExpanded={open}
                                label={t("chatBot.search")}
                                icon="search"
                                tooltip={t("chatBot.search")}
                                onClick={() => navigationClicked("/search-result")}
                            />)}
                            <div id="tileTour10">
                                <SidebarBtn
                                    isExpanded={open}
                                    label={t("chatBot.newChat")}
                                    icon="newchat"
                                    tooltip={t("chatBot.newChat")}
                                    onClick={() => navigationClicked("/ai")}
                                />
                                <SidebarBtn
                                    isExpanded={open}
                                    label={t("chatBot.recentChats")}
                                    onClick={() => setOpen(true)}
                                    icon={`recentChat`}
                                    tooltip={t("chatBot.recentChats")}
                                    noCursor={open}
                                    deleteIcon
                                />
                            </div>
                        </div>
                        <div>
                            {open ? sidebar : null}
                        </div>
                    </div>
                    <div className="sidebar-footer">
                        <SidebarBtn
                            isExpanded={open}
                            label={t("feedbackWidget")}
                            onClick={handleClick}
                            icon="feedback"
                            tooltip={t("feedbackWidget")}
                        />
                    </div>
                </aside>
            </FocusTrap>
            {open && (
                <div
                    id="sidebar-backdrop"
                    className="backdrop"
                    onClick={() => setOpen(false)}
                    data-testid="backdrop"
                />
            )}
            <div className={`main-content ${open ? "shifted" : ""}`}>
                <div className="me8eon-responsive-container">
                    {children}
                </div>
            </div>
        </div>
    );
}
